import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Avatar,
  InputAdornment,
  Collapse,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from '@mui/lab';
import {
  Build as WorkOrderIcon,
  Build as BuildIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Business as VendorIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Timer as PendingIcon,
  Cancel as CancelIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Note as NoteIcon,
  Alarm as AlertIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachIcon,
  Payment as CostIcon,
  Security as SafetyIcon,
  Star as RatingIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  Assessment as ReportIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Gavel as ApprovalIcon,
  Engineering as MaintenanceIcon,
  Warning as EmergencyIcon,
  Home as SiteIcon,
  MonetizationOn as MoneyIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workorders-tabpanel-${index}`}
      aria-labelledby={`workorders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WorkOrdersPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<any>(null);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [dashboardData, setDashboardData] = useState<any>({});
  
  // UI states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [workOrderFilter, setWorkOrderFilter] = useState({
    status: 'all',
    type: 'all',
    priority: 'all',
    assignee: 'all'
  });
  const [vendorFilter, setVendorFilter] = useState({
    type: 'all',
    status: 'active',
    rating: '0'
  });
  const [expandedWorkOrder, setExpandedWorkOrder] = useState<string | null>(null);
  
  // Dialog states
  const [workOrderDialog, setWorkOrderDialog] = useState(false);
  const [vendorDialog, setVendorDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [commentDialog, setCommentDialog] = useState(false);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [laborDialog, setLaborDialog] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(false);
  
  // Form states
  const [workOrderForm, setWorkOrderForm] = useState({
    title: '',
    description: '',
    type: 'maintenance',
    category: 'site_infrastructure',
    priority: 'medium',
    location: {
      type: 'general',
      site: null as any,
      description: ''
    },
    scheduling: {
      requestedDate: null as any,
      estimatedHours: ''
    }
  });
  
  const [vendorForm, setVendorForm] = useState({
    name: '',
    type: 'general_contractor',
    contact: {
      primary: {
        name: '',
        phone: '',
        email: ''
      }
    },
    services: [{
      category: '',
      description: '',
      hourlyRate: ''
    }]
  });
  
  const [assignForm, setAssignForm] = useState({
    assignTo: 'user',
    user: null as any,
    vendor: null as any,
    scheduledStart: null as any,
    scheduledEnd: null as any,
    notes: ''
  });
  
  const [statusForm, setStatusForm] = useState({
    status: '',
    notes: ''
  });
  
  const [commentForm, setCommentForm] = useState({
    content: '',
    type: 'comment',
    internal: false
  });

  useEffect(() => {
    fetchData();
  }, [workOrderFilter, vendorFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchWorkOrders(),
        fetchVendors(),
        fetchDashboardData(),
        fetchSites(),
        fetchUsers()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (workOrderFilter.status !== 'all') params.status = workOrderFilter.status;
      if (workOrderFilter.type !== 'all') params.type = workOrderFilter.type;
      if (workOrderFilter.priority !== 'all') params.priority = workOrderFilter.priority;
      if (workOrderFilter.assignee !== 'all') params.assignee = workOrderFilter.assignee;
      
      const response = await api.get('/work-orders', { params });
      setWorkOrders(response.data.workOrders);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const params: any = {};
      if (vendorFilter.type !== 'all') params.type = vendorFilter.type;
      if (vendorFilter.status !== 'all') params.status = vendorFilter.status;
      if (parseFloat(vendorFilter.rating) > 0) params.rating = vendorFilter.rating;
      
      const response = await api.get('/work-orders/vendors', { params 
        }
      );
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [
        activeResponse,
        overdueResponse,
        pendingResponse,
        metricsResponse
      ] = await Promise.all([
        api.get('/work-orders/active'),
        api.get('/work-orders/status/overdue'),
        api.get('/work-orders/status/pending-approval'),
        api.get('/work-orders/vendors/metrics')
      ]);
      
      setDashboardData({
        activeOrders: activeResponse.data.length,
        overdueOrders: overdueResponse.data.length,
        pendingApprovals: pendingResponse.data.length,
        vendorMetrics: metricsResponse.data
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateWorkOrder = async () => {
    try {
      await api.post('/work-orders', {
        ...workOrderForm,
        scheduling: {
          ...workOrderForm.scheduling,
          estimatedHours: parseFloat(workOrderForm.scheduling.estimatedHours)
        }
      });
      
      setWorkOrderDialog(false);
      fetchData();
      alert('Work order created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error creating work order');
    }
  };

  const handleCreateVendor = async () => {
    try {
      await api.post('/work-orders/vendors', {
        ...vendorForm,
        services: vendorForm.services.map(s => ({
          ...s,
          hourlyRate: parseFloat(s.hourlyRate)
        }))
      });
      
      setVendorDialog(false);
      fetchData();
      alert('Vendor created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error creating vendor');
    }
  };

  const handleAssignWorkOrder = async () => {
    try {
      await api.post(`/work-orders/${selectedWorkOrder._id}/assign`, assignForm);
      
      setAssignDialog(false);
      fetchData();
      alert('Work order assigned successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error assigning work order');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await api.post(`/work-orders/${selectedWorkOrder._id}/status`, statusForm);
      
      setStatusDialog(false);
      fetchData();
      alert('Status updated successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error updating status');
    }
  };

  const handleAddComment = async () => {
    try {
      await api.post(`/work-orders/${selectedWorkOrder._id}/comments`, commentForm);
      
      setCommentDialog(false);
      fetchData();
      alert('Comment added successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding comment');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'draft': 'default',
      'submitted': 'info',
      'approved': 'primary',
      'scheduled': 'warning',
      'in_progress': 'secondary',
      'on_hold': 'warning',
      'completed': 'success',
      'cancelled': 'error',
      'rejected': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      'low': 'info',
      'medium': 'primary',
      'high': 'warning',
      'urgent': 'error',
      'emergency': 'error'
    };
    return colors[priority] || 'default';
  };

  const getTypeIcon = (type: string) => {
    const icons: any = {
      'maintenance': <MaintenanceIcon />,
      'repair': <BuildIcon />,
      'inspection': <ViewIcon />,
      'preventive': <ScheduleIcon />,
      'emergency': <EmergencyIcon />,
      'installation': <AddIcon />,
      'upgrade': <TrendingIcon />
    };
    return icons[type] || <TaskIcon />;
  };

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode, color?: string) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" color={color}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderWorkOrderTimeline = (workOrder: any) => (
    <Timeline position="alternate">
      <TimelineItem>
        <TimelineSeparator>
          <TimelineDot color="primary">
            <TaskIcon />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Typography variant="body2">
            Work Order Created
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(workOrder.createdAt).format('MMM D, YYYY h:mm A')}
          </Typography>
        </TimelineContent>
      </TimelineItem>
      
      {workOrder.assignedTo?.assignedDate && (
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color="info">
              <PersonIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Assigned
            </Typography>
            <Typography variant="caption">
              {dayjs(workOrder.assignedTo.assignedDate).format('MMM D, YYYY h:mm A')}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
      
      {workOrder.scheduling?.actualStart && (
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color="warning">
              <StartIcon />
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Work Started
            </Typography>
            <Typography variant="caption">
              {dayjs(workOrder.scheduling.actualStart).format('MMM D, YYYY h:mm A')}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
      
      {workOrder.completion?.completedDate && (
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color="success">
              <CheckIcon />
            </TimelineDot>
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body2">
              Completed
            </Typography>
            <Typography variant="caption">
              {dayjs(workOrder.completion.completedDate).format('MMM D, YYYY h:mm A')}
            </Typography>
          </TimelineContent>
        </TimelineItem>
      )}
    </Timeline>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Work Order Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track maintenance, repairs, and vendor management
        </Typography>
      </Box>

      {/* Dashboard Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Active Orders',
            dashboardData.activeOrders || 0,
            'Currently in progress',
            <TaskIcon color="primary" />
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Overdue Orders',
            dashboardData.overdueOrders || 0,
            'Need attention',
            <WarningIcon color="error" />,
            'error.main'
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Pending Approvals',
            dashboardData.pendingApprovals || 0,
            'Awaiting approval',
            <ApprovalIcon color="warning" />,
            'warning.main'
          )}
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Active Vendors',
            dashboardData.vendorMetrics?.totalVendors || 0,
            'Registered vendors',
            <VendorIcon color="info" />
          )}
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Work Orders" icon={<TaskIcon />} iconPosition="start" />
          <Tab label="Vendors" icon={<VendorIcon />} iconPosition="start" />
          <Tab label="Schedule" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Reports" icon={<ReportIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Work Orders Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={workOrderFilter.status}
                    onChange={(e) => setWorkOrderFilter({ ...workOrderFilter, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="submitted">Submitted</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={workOrderFilter.type}
                    onChange={(e) => setWorkOrderFilter({ ...workOrderFilter, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                    <MenuItem value="preventive">Preventive</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={workOrderFilter.priority}
                    onChange={(e) => setWorkOrderFilter({ ...workOrderFilter, priority: e.target.value })}
                    label="Priority"
                  >
                    <MenuItem value="all">All Priority</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setWorkOrderDialog(true)}
              >
                New Work Order
              </Button>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workOrders.map((workOrder) => (
                  <React.Fragment key={workOrder._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedWorkOrder(
                            expandedWorkOrder === workOrder._id ? null : workOrder._id
                          )}
                        >
                          {expandedWorkOrder === workOrder._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{workOrder.workOrderId}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTypeIcon(workOrder.type)}
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="body2">
                              {workOrder.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {workOrder.category}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.priority}
                          size="small"
                          color={getPriorityColor(workOrder.priority)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workOrder.status}
                          size="small"
                          color={getStatusColor(workOrder.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {workOrder.assignedTo?.user ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">
                              {workOrder.assignedTo.user.firstName} {workOrder.assignedTo.user.lastName}
                            </Typography>
                          </Box>
                        ) : workOrder.assignedTo?.vendor ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VendorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">
                              {workOrder.assignedTo.vendor.name}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {workOrder.scheduling?.scheduledEnd ? (
                          <Typography variant="body2">
                            {dayjs(workOrder.scheduling.scheduledEnd).format('MM/DD/YYYY')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not scheduled
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Assign">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedWorkOrder(workOrder);
                                setAssignDialog(true);
                              }}
                            >
                              <PersonIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedWorkOrder(workOrder);
                                setStatusDialog(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Comment">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedWorkOrder(workOrder);
                                setCommentDialog(true);
                              }}
                            >
                              <NoteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0 }}>
                        <Collapse in={expandedWorkOrder === workOrder._id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Description
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {workOrder.description}
                                </Typography>
                                
                                <Typography variant="subtitle2" gutterBottom>
                                  Location
                                </Typography>
                                <Typography variant="body2">
                                  {workOrder.location?.description || workOrder.location?.type || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Timeline
                                </Typography>
                                {renderWorkOrderTimeline(workOrder)}
                              </Grid>
                            </Grid>
                            
                            {workOrder.comments && workOrder.comments.length > 0 && (
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Recent Comments
                                </Typography>
                                <List dense>
                                  {workOrder.comments.slice(-3).map((comment: any, index: number) => (
                                    <ListItem key={index}>
                                      <ListItemText
                                        primary={comment.content}
                                        secondary={`${comment.user?.firstName} ${comment.user?.lastName} - ${dayjs(comment.timestamp).format('MMM D, h:mm A')}`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={100} // This should come from API
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Vendors Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={vendorFilter.type}
                    onChange={(e) => setVendorFilter({ ...vendorFilter, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="general_contractor">General Contractor</MenuItem>
                    <MenuItem value="electrical">Electrical</MenuItem>
                    <MenuItem value="plumbing">Plumbing</MenuItem>
                    <MenuItem value="hvac">HVAC</MenuItem>
                    <MenuItem value="landscaping">Landscaping</MenuItem>
                    <MenuItem value="cleaning">Cleaning</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={vendorFilter.status}
                    onChange={(e) => setVendorFilter({ ...vendorFilter, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="pending_approval">Pending</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Min Rating</InputLabel>
                  <Select
                    value={vendorFilter.rating}
                    onChange={(e) => setVendorFilter({ ...vendorFilter, rating: e.target.value })}
                    label="Min Rating"
                  >
                    <MenuItem value="0">All Ratings</MenuItem>
                    <MenuItem value="3">3+ Stars</MenuItem>
                    <MenuItem value="4">4+ Stars</MenuItem>
                    <MenuItem value="4.5">4.5+ Stars</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setVendorDialog(true)}
              >
                New Vendor
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {vendors.map((vendor) => (
              <Grid item xs={12} md={6} lg={4} key={vendor._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {vendor.companyName || vendor.name || 'Unknown Vendor'}
                      </Typography>
                      <Chip
                        label={vendor.status || 'unknown'}
                        size="small"
                        color={vendor.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {vendor.vendorType?.replace('_', ' ').toUpperCase() || vendor.category?.replace('_', ' ').toUpperCase() || 'Unknown Type'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <RatingIcon sx={{ color: 'gold', fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {vendor.performance?.overallRating?.toFixed(1) || 'No rating'} 
                        ({vendor.performance?.totalRatings || 0} reviews)
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {vendor.primaryContact?.phone || 'No phone'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {vendor.primaryContact?.email || 'No email'}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      Capacity: {vendor.performance?.metrics?.totalOrders || 0} total orders
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button size="small" onClick={() => setSelectedVendor(vendor)}>
                      View Details
                    </Button>
                    <IconButton size="small">
                      <EmailIcon />
                    </IconButton>
                    <IconButton size="small">
                      <PhoneIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Schedule Tab */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Maintenance Schedule
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calendar view of scheduled work orders and maintenance tasks
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Reports Tab */}
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ReportIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Work Order Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analytics and reporting for maintenance operations
            </Typography>
          </Box>
        </TabPanel>
      </Paper>

      {/* Create Work Order Dialog */}
      <Dialog open={workOrderDialog} onClose={() => setWorkOrderDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Title"
              value={workOrderForm.title}
              onChange={(e) => setWorkOrderForm({ ...workOrderForm, title: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Description"
              value={workOrderForm.description}
              onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={workOrderForm.type}
                    onChange={(e) => setWorkOrderForm({ ...workOrderForm, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                    <MenuItem value="preventive">Preventive</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                    <MenuItem value="installation">Installation</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={workOrderForm.category}
                    onChange={(e) => setWorkOrderForm({ ...workOrderForm, category: e.target.value })}
                    label="Category"
                  >
                    <MenuItem value="site_infrastructure">Site Infrastructure</MenuItem>
                    <MenuItem value="utilities">Utilities</MenuItem>
                    <MenuItem value="buildings">Buildings</MenuItem>
                    <MenuItem value="amenities">Amenities</MenuItem>
                    <MenuItem value="grounds">Grounds</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={workOrderForm.priority}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, priority: e.target.value })}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Location Type</InputLabel>
                  <Select
                    value={workOrderForm.location.type}
                    onChange={(e) => setWorkOrderForm({
                      ...workOrderForm,
                      location: { ...workOrderForm.location, type: e.target.value }
                    })}
                    label="Location Type"
                  >
                    <MenuItem value="site">Site</MenuItem>
                    <MenuItem value="building">Building</MenuItem>
                    <MenuItem value="amenity">Amenity</MenuItem>
                    <MenuItem value="grounds">Grounds</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {workOrderForm.location.type === 'site' && (
                  <Autocomplete
                    options={sites}
                    getOptionLabel={(option) => `Site ${option.number} (${option.type})`}
                    value={workOrderForm.location.site}
                    onChange={(e, value) => setWorkOrderForm({
                      ...workOrderForm,
                      location: { ...workOrderForm.location, site: value }
                    })}
                    renderInput={(params) => (
                      <TextField {...params} label="Site" />
                    )}
                  />
                )}
              </Grid>
            </Grid>
            
            <TextField
              label="Location Description"
              value={workOrderForm.location.description}
              onChange={(e) => setWorkOrderForm({
                ...workOrderForm,
                location: { ...workOrderForm.location, description: e.target.value }
              })}
              fullWidth
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Requested Date"
                  value={workOrderForm.scheduling.requestedDate}
                  onChange={(date) => setWorkOrderForm({
                    ...workOrderForm,
                    scheduling: { ...workOrderForm.scheduling, requestedDate: date }
                  })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Estimated Hours"
                  type="number"
                  value={workOrderForm.scheduling.estimatedHours}
                  onChange={(e) => setWorkOrderForm({
                    ...workOrderForm,
                    scheduling: { ...workOrderForm.scheduling, estimatedHours: e.target.value }
                  })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkOrderDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateWorkOrder} 
            variant="contained"
            disabled={!workOrderForm.title || !workOrderForm.description}
          >
            Create Work Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Work Order Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Work Order</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <ToggleButtonGroup
              value={assignForm.assignTo}
              exclusive
              onChange={(e, value) => value && setAssignForm({ ...assignForm, assignTo: value })}
              fullWidth
            >
              <ToggleButton value="user">
                <PersonIcon sx={{ mr: 1 }} />
                Internal Staff
              </ToggleButton>
              <ToggleButton value="vendor">
                <VendorIcon sx={{ mr: 1 }} />
                Vendor
              </ToggleButton>
            </ToggleButtonGroup>
            
            {assignForm.assignTo === 'user' ? (
              <Autocomplete
                options={users.filter(u => u.role === 'maintenance' || u.role === 'technician')}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                value={assignForm.user}
                onChange={(e, value) => setAssignForm({ ...assignForm, user: value })}
                renderInput={(params) => (
                  <TextField {...params} label="Staff Member" required />
                )}
              />
            ) : (
              <Autocomplete
                options={vendors.filter(v => v.status === 'active')}
                getOptionLabel={(option) => option.name}
                value={assignForm.vendor}
                onChange={(e, value) => setAssignForm({ ...assignForm, vendor: value })}
                renderInput={(params) => (
                  <TextField {...params} label="Vendor" required />
                )}
              />
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Scheduled Start"
                  value={assignForm.scheduledStart}
                  onChange={(date) => setAssignForm({ ...assignForm, scheduledStart: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Scheduled End"
                  value={assignForm.scheduledEnd}
                  onChange={(date) => setAssignForm({ ...assignForm, scheduledEnd: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
            
            <TextField
              label="Assignment Notes"
              value={assignForm.notes}
              onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignWorkOrder} 
            variant="contained"
            disabled={assignForm.assignTo === 'user' ? !assignForm.user : !assignForm.vendor}
          >
            Assign Work Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Work Order Status</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                label="New Status"
              >
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Status Notes"
              value={statusForm.notes}
              onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Add notes about this status change..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
            disabled={!statusForm.status}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialog} onClose={() => setCommentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Comment"
              value={commentForm.content}
              onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
            />
            
            <FormControl fullWidth>
              <InputLabel>Comment Type</InputLabel>
              <Select
                value={commentForm.type}
                onChange={(e) => setCommentForm({ ...commentForm, type: e.target.value })}
                label="Comment Type"
              >
                <MenuItem value="comment">General Comment</MenuItem>
                <MenuItem value="status_change">Status Update</MenuItem>
                <MenuItem value="completion">Completion Note</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddComment} 
            variant="contained"
            disabled={!commentForm.content}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkOrdersPage;